import ReactEcharts from 'echarts-for-react'

import {
  InformationIcon,
  Leaderboard,
  ScalrTableColumn,
} from '@/components/customComponents'
import {
  PersonaLeaderboardModel,
  PromoSetModel,
  TopCampaignsModel,
} from '@/types/clarity'

const personaLeaderboardData: PersonaLeaderboardModel[] = [
  {
    persona: 'Everyday Loyalist',
    salesPercentage: 30.5,
    weeklyVisitFrequency: 4.5,
    aov: 13.8,
    retentionPercentage: 88.0,
    discountPercentage: 4.2,
  },
  {
    persona: 'Morning Regulars',
    salesPercentage: 22.3,
    weeklyVisitFrequency: 3.9,
    aov: 12.6,
    retentionPercentage: 84.2,
    discountPercentage: 3.6,
  },
  {
    persona: 'Value Hunters',
    salesPercentage: 15.1,
    weeklyVisitFrequency: 2.1,
    aov: 9.4,
    retentionPercentage: 67.5,
    discountPercentage: 12.5,
  },
  {
    persona: 'Product Explorers',
    salesPercentage: 10.4,
    weeklyVisitFrequency: 1.8,
    aov: 14.7,
    retentionPercentage: 72.1,
    discountPercentage: 6.1,
  },
  {
    persona: 'Social Weekenders',
    salesPercentage: 13.2,
    weeklyVisitFrequency: 1.5,
    aov: 16.1,
    retentionPercentage: 68.9,
    discountPercentage: 5.4,
  },
  {
    persona: 'Occasional Indulgers',
    salesPercentage: 8.5,
    weeklyVisitFrequency: 0.9,
    aov: 18.75,
    retentionPercentage: 61.0,
    discountPercentage: 2.8,
  },
]

const topCampaignsData: TopCampaignsModel[] = [
  {
    campaignName: 'Churn',
    orderSharePercentage: 6.5,
    aov: 13.2,
    discountPercentage: 18.0,
  },
  {
    campaignName: 'Upsell - Frequency',
    orderSharePercentage: 12.3,
    aov: 14.9,
    discountPercentage: 12.5,
  },
  {
    campaignName: 'Upsell - Basket',
    orderSharePercentage: 10.1,
    aov: 16.8,
    discountPercentage: 10.9,
  },
  {
    campaignName: 'Acquisition',
    orderSharePercentage: 9.4,
    aov: 11.5,
    discountPercentage: 22.3,
  },
  {
    campaignName: 'Retention',
    orderSharePercentage: 18.7,
    aov: 15.1,
    discountPercentage: 14.1,
  },
]

const promoSetsData: PromoSetModel[] = [
  {
    promoName: '20% Off Espressos',
    orderSharePercentage: 10,
    changePercentage: 6,
  },
  {
    promoName: 'Buy 4 Get 1 Free',
    orderSharePercentage: 8,
    changePercentage: 8,
  },
  {
    promoName: 'After Hours',
    orderSharePercentage: 7,
    changePercentage: 10,
  },
  {
    promoName: 'New Comer',
    orderSharePercentage: 4,
    changePercentage: 11,
  },
  {
    promoName: '$2 Off Any Drink',
    orderSharePercentage: 3,
    changePercentage: 6,
  },
]

const personaLeaderboardColumns: ScalrTableColumn[] = [
  { header: 'Persona', accessorKey: 'persona', type: 'text' },
  { header: 'Sales %', accessorKey: 'salesPercentage', type: 'percentage' },
  {
    header: 'Weekly Visit Frequency',
    accessorKey: 'weeklyVisitFrequency',
    type: 'number',
  },
  { header: 'AOV', accessorKey: 'aov', type: 'number' },
  {
    header: 'Retention %',
    accessorKey: 'retentionPercentage',
    type: 'percentage',
  },
  {
    header: 'Discount %',
    accessorKey: 'discountPercentage',
    type: 'percentage',
  },
]

const topCampaignsColumns: ScalrTableColumn[] = [
  { header: 'Campaign', accessorKey: 'campaignName', type: 'text' },
  {
    header: 'Order Share %',
    accessorKey: 'orderSharePercentage',
    type: 'percentage',
  },
  { header: 'AOV', accessorKey: 'aov', type: 'money' },
  {
    header: 'Discount %',
    accessorKey: 'discountPercentage',
    type: 'percentage',
  },
]

const promoSetsColumns: ScalrTableColumn[] = [
  { header: 'Promo', accessorKey: 'promoName', type: 'text' },
  {
    header: 'Order Share %',
    accessorKey: 'orderSharePercentage',
    type: 'percentage',
  },
  { header: 'Change %', accessorKey: 'changePercentage', type: 'percentage' },
]

export function TempLoyaltyChart({
  first,
  second,
}: {
  first: boolean
  second: boolean
}) {
  return (
    <div>
      {first && (
        <div className="grid grid-cols-12 gap-4">
          <div className="p-2 col-span-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold">Frequency</h3>
            </div>
            <div className="grid grid-cols-2">
              <ReactEcharts option={getFrequencyChart7DaysData()} />
              <ReactEcharts option={getFrequencyChart30DaysData()} />
            </div>
          </div>
          <div className="p-2 col-span-3">
            <div className="flex justify-between items-center mb-2">
              <div className="flex flex-row items-center gap-2">
                <h3 className="text-lg font-bold">Churn Rate</h3>
                <InformationIcon description="The rate at which customers stop purchasing over a certain period" />
              </div>
            </div>
            <ReactEcharts
              option={getChurnRateChartData({
                churnRate: 71,
                reactivationRate: 61,
                daysBetween: 55,
              })}
            />
          </div>
          <div className="p-2 col-span-3">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold">Order Count Brackets</h3>
            </div>
            <ReactEcharts option={getOrderCountBracketsChartData()} />
          </div>
        </div>
      )}
      {!first && (
        <>
          {second && (
            <div className="grid grid-cols-2 gap-4">
              <Leaderboard
                title="Promo Sets"
                data={promoSetsData}
                columns={promoSetsColumns}
                loading={false}
              />
              <Leaderboard
                title="Top Campaigns"
                data={topCampaignsData}
                columns={topCampaignsColumns}
                loading={false}
              />
            </div>
          )}
          {!second && (
            <Leaderboard
              title="Persona Leaderboard"
              data={personaLeaderboardData}
              columns={personaLeaderboardColumns}
              loading={false}
              sortBy="salesPercentage"
            />
          )}
        </>
      )}
    </div>
  )
}

const getFrequencyChart7DaysData = () => {
  return {
    legend: {
      data: ['7 days'],
    },
    grid: {
      left: '3%',
      right: '15%',
      bottom: '5%',
      top: '10%',
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
    },
    xAxis: {
      show: false,
      type: 'value',
    },
    yAxis: {
      type: 'category',
      data: [
        'Two Notch',
        'Taylors',
        'Lexington',
        'Columbia',
        'Rock Hill',
        'Greensboro',
        'Clemmons',
        'Winston-Salem',
        'Williamson',
        'Plaza',
      ],
      axisLabel: {
        show: true,
      },
    },
    series: [
      {
        name: '7 days',
        type: 'bar',
        data: [1.4, 1.57, 1.61, 1.53, 1.67, 1.7, 1.61, 1.67, 1.66, 1.73],
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#333',
          fontWeight: 'bold',
        },
      },
    ],
    center: ['50%', '60%'],
  }
}

const getFrequencyChart30DaysData = () => {
  return {
    legend: {
      data: ['30 days'],
    },
    tooltip: {
      trigger: 'axis',
    },
    grid: {
      left: '3%',
      right: '15%',
      bottom: '5%',
      top: '10%',
      containLabel: true,
    },
    xAxis: {
      show: false,
      type: 'value',
    },
    yAxis: {
      type: 'category',
      data: [
        'Two Notch',
        'Taylors',
        'Lexington',
        'Columbia',
        'Rock Hill',
        'Greensboro',
        'Clemmons',
        'Winston-Salem',
        'Williamson',
        'Plaza',
      ],
    },
    series: [
      {
        name: '30 days',
        type: 'bar',
        data: [1.55, 2.76, 3.02, 2.83, 3.08, 3.28, 3.06, 3.12, 3.02, 3.32],
        itemStyle: {
          color: 'green',
        },
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#333',
          fontWeight: 'bold',
        },
      },
    ],
    center: ['50%', '60%'],
  }
}

const getChurnRateChartData = ({
  churnRate,
  reactivationRate,
  daysBetween,
}: {
  churnRate: number
  reactivationRate: number
  daysBetween: number
}) => {
  const returnDays = Math.ceil(Math.max(churnRate, reactivationRate) / 10) * 10

  return {
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: [
        '30-day life time churn',
        'Reactivation % of customers that churned',
      ],
      axisLabel: {
        interval: 0,
        rotate: 0,
        width: 80,
        overflow: 'break',
        lineHeight: 14,
      },
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        type: 'bar',
        data: [
          {
            value: churnRate,
            itemStyle: { color: '#ff6b6b' },
          },
          {
            value: reactivationRate,
            itemStyle: { color: '#4ecdc4' },
          },
        ],
        label: {
          show: true,
          position: 'top',
          formatter: '{c}%',
          fontSize: 14,
          fontWeight: 'bold',
        },
        barWidth: '40%',
      },
      {
        type: 'line',
        markLine: {
          symbol: 'none',
          label: {
            formatter: `- ${daysBetween} days -`,
            position: 'middle',
            fontSize: 12,
            color: '#111',
            fontWeight: 'bold',
          },
          lineStyle: {
            color: 'transparent',
          },
          data: [{ yAxis: returnDays / 2 }],
        },
      },
    ],
  }
}

const getOrderCountBracketsChartData = () => {
  const brackets = [32, 11, 15, 11, 13, 19]
  const churnRate = [18, 9, 13, 11, 17, 10]

  return {
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c}%',
    },
    grid: {
      left: '0',
      right: '-10%',
      bottom: '0',
      top: '5%',
      containLabel: true,
    },
    legend: {
      data: ['Current'],
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      type: 'category',
      data: ['9+', '7 to 9', '4 to 6', '3', '2', '1'],
      axisLabel: {
        show: true,
      },
    },
    series: [
      // brackets
      {
        type: 'bar',
        data: brackets,
        stack: 'total',
        label: {
          show: true,
          position: 'inside',
          formatter: '{c}%',
          fontSize: 14,
          color: '#fff',
          fontWeight: 'bold',
        },
      },
      {
        type: 'bar',
        data: churnRate,
        stack: 'total',
        itemStyle: {
          color: 'transparent',
        },
        label: {
          show: true,
          position: 'insideLeft',
          formatter: '{c}%',
          fontSize: 14,
          color: '#333',
          fontWeight: 'bold',
        },
      },
    ],
  }
}
